import {
  BackEndValueType,
  ECardinality,
  EProjectType,
  ESdChangeReason,
  ESymbolCategory,
  IComponentDefinition,
  IDisplayItem,
  IProperty,
  ISymbol,
  PropertyToolType
} from "@kuika/kuika-cl-model";
import {
  BreadcrumbOptions,
  CalendarOptions,
  MenuOptions,
  MixedChartLineOptions,
  MixedChartOptions,
  MixedChartPointOptions,
  RadialChartLevelOptions,
  RadialChartOptions,
  TimelineOptions
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import type { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import {
  FA_ICON_TYPES,
  IconLibrary,
  IconType,
  isFontAwesomeIconType,
  isMaterialIconType,
  MATERIAL_ICON_TYPES
} from "../../../../shared/icon/k-icon";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { normalizeAcceptValue } from "../../../../shared/utility/accept-file-types";
import { ColorPickerPositionHelper } from "../../../../shared/utility/color-picker-position-helper";
import { formatQualifiedName } from "../../../../shared/utility/datasource-name-qualifier";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import {
  listDesignerMicroAppProjectScreens,
  listDesignerMicroAppProjects,
  listDesignerMicroAppScreenInputs,
  setDesignerMicroAppProject,
  setDesignerMicroAppStartingScreen,
  setDesignerPropertySymbol,
  setDesignerPropertyType,
  setDesignerPropertyTypeOptions,
  setDesignerPropertyValue
} from "../../../../shared/utility/designer-property-actions";
import {
  DesignerPropertyDataFieldEditor,
  DesignerPropertyDataEditorOption,
  DesignerPropertyDataEditorState,
  getDesignerPropertyDataEditorState,
  setDesignerPropertyDataAction,
  setDesignerPropertyDataField
} from "../../../../shared/utility/designer-property-data-editor-actions";
import { tryResolveEditTarget } from "../../../../shared/utility/detached-screen-session";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { PropertyHelper } from "../../../../shared/utility/property-helper";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaTreeSelect, { ITreeSelectNode } from "../../../ui-kit/kuika-tree-select/kuika-tree-select";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { ColorPicker } from "../styling-panel/styling-color-picker";
import { DataKeyTextEditor } from "./data-key-editor/data-key-text-editor";
import { PropertyFormColorPickerArrayProperty } from "./property-form-color-picker-array-property";
import { PropertyFormDynamicInputFormatter } from "./property-form-dynamic-input-formatter";
import { PropertyFormEnumProperty } from "./property-form-enum-property";
import { PropertyFormIcon } from "./property-form-icon";
import { PropertyFormSelectProperty } from "./property-form-select-property";
import "./property-form-item.scss";
import { SymbolPicker } from "./symbol-picker";

export interface IPropertyFormItemState {
  previousActionDefinitionId: string | undefined;
  isStartingScreenOptionsLoading: boolean;
  startingScreenOptions: { value: string; label: string }[];
  screenInputs: { name: string; typeName: string; cardinality: number }[];
  isScreenInputsLoading: boolean;
  projectSearchText: string;
  /** Key of the chart color editor whose fixed-color picker is currently open (one at a time). */
  activeColorEditorKey?: string;
}

export interface IPropertyFormItemProps {
  toggleJsonModal: (property: IProperty) => void;
  property: IProperty;
  kuikaId: string;
  /**
   * The screen this row edits, when it is NOT the one the designer has open.
   *
   * The workflow screen builder edits the task screen of whichever step is selected, and that
   * screen is almost never on the canvas (KOA-18431). Absent — the ordinary property panel — every
   * read and write below behaves exactly as before.
   */
  screenId?: string | null;
  /**
   * Fired after a write lands, with the component and property that changed.
   *
   * The screen builder stages what it owes the server as operations, so it has to know that a
   * property was configured; it cannot diff a tree it does not own.
   */
  onPropertyChanged?: (kuikaId: string, propertyName: string) => void;
  /**
   * Bumped by the host after a write landed on a screen the designer does NOT have open.
   *
   * That screen's tree is a plain in-memory object, so a write to it changes no context and the
   * SD-driven update gate would keep the editor showing the pre-write value. The open-screen panel
   * never needs this — its writes dispatch a change reason the gate already recognises.
   */
  refreshToken?: number;
}

class PropertyFormItem extends PureSdComponent<
  IPropertyFormItemProps & IWithSDProps & IWithContextProp,
  IPropertyFormItemState
> {
  propertyHelper: PropertyHelper;

  propertySdHelper: PropertySdHelper;

  constructor(props: IPropertyFormItemProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertyHelper = new PropertyHelper();
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);

    this.state = {
      previousActionDefinitionId: props.property.symbol?.actionDefinitionId ?? undefined,
      startingScreenOptions: [],
      isStartingScreenOptionsLoading: false,
      screenInputs: [],
      isScreenInputsLoading: false,
      projectSearchText: ""
    };
  }

  componentDidMount() {
    const calculatedPropertyToolType: PropertyToolType = this.calculatePropertyToolType();

    if (calculatedPropertyToolType === "MicroAppProjectProp") {
      this.loadProjects();
    }

    if (calculatedPropertyToolType === "MicroAppStartingScreenProp") {
      const selectedProjectId = this.getSelectedProjectId();
      if (selectedProjectId) {
        this.loadProjectScreens(selectedProjectId);
      }

      const selectedStartingScreen = this.getStartingScreenValue();
      if (selectedStartingScreen) {
        this.loadScreenInputs(selectedStartingScreen);
      } else {
        this.setState({ screenInputs: [], isScreenInputsLoading: false });
      }
    }
  }

  loadProjects = async () => {
    await listDesignerMicroAppProjects(getSdInstance());
    this.forceUpdate();
  };

  loadProjectScreens = async (projectId: string) => {
    this.setState({ isStartingScreenOptionsLoading: true });
    if (!projectId) {
      this.setState({ startingScreenOptions: [], isStartingScreenOptionsLoading: false });
      return;
    }

    const result = await listDesignerMicroAppProjectScreens(getSdInstance(), { projectId });
    if (result?.success) {
      this.setState({
        startingScreenOptions: result.options || [],
        isStartingScreenOptionsLoading: false
      });
      return;
    }

    this.setState({ startingScreenOptions: [], isStartingScreenOptionsLoading: false });
  };

  loadScreenInputs = async (screenId: string) => {
    this.setState({ isScreenInputsLoading: true });
    if (!screenId) {
      this.setState({ screenInputs: [], isScreenInputsLoading: false });
      return;
    }

    const result = await listDesignerMicroAppScreenInputs(getSdInstance(), {
      screenId,
      kuikaId: this.props.kuikaId
    });
    this.setState({
      screenInputs: result?.inputs || [],
      isScreenInputsLoading: false
    });
  };

  /**
   * The context the platform's property helper reads through.
   *
   * With no `screenId` this is the open screen, unchanged. With one, it is that screen — and if
   * that screen is gone the helper must NOT be handed the open screen instead, so this throws.
   * `render` bails out before that happens; only a stale async callback could reach it.
   */
  getScreenDesignContextProviderValues = (): IScreenDesignContextState => {
    if (!this.props.screenId) return this.props.sd;
    const target: IScreenDesignContextState | undefined = this.sdTarget;
    if (target === undefined) {
      throw new Error(`Screen "${this.props.screenId}" is no longer loaded, so its properties cannot be read.`);
    }
    return target;
  };

  /**
   * The screen state this row reads.
   *
   * Without `screenId` it is the open screen — the same object the panel has always used.
   * With one, it is that screen's own state, and `undefined` when the screen is no longer loaded:
   * `render` shows the absence rather than throwing, and never falls back to the open screen,
   * because reading one screen while the user is looking at another is the failure this whole
   * seam exists to prevent.
   */
  private get sdTarget(): IScreenDesignContextState | undefined {
    if (!this.props.screenId) return this.props.sd;
    return tryResolveEditTarget(getSdInstance(), this.props.screenId);
  }

  /** Report a landed write to whoever is staging changes for this screen. */
  private notifyChanged = (propertyName: string = this.props.property.name): void => {
    this.props.onPropertyChanged?.(this.props.kuikaId, propertyName);
  };

  /**
   * Every write in this row goes through one of these wrappers, so the screen it addresses and the
   * change report are decided in ONE place instead of at thirteen call sites.
   */
  private get canWrite(): boolean {
    return this.sdTarget !== undefined;
  }

  private writeValue = (propertyName: string, value: unknown, changeReason?: ESdChangeReason): void => {
    if (!this.canWrite) return;
    const input = { propertyName, value, kuikaId: this.props.kuikaId, screenId: this.props.screenId };
    if (changeReason === undefined) setDesignerPropertyValue(getSdInstance(), input);
    else setDesignerPropertyValue(getSdInstance(), input, changeReason);
    this.notifyChanged(propertyName);
  };

  private writeSymbol = (propertyName: string, symbol: ISymbol, changeReason: ESdChangeReason): void => {
    if (!this.canWrite) return;
    setDesignerPropertySymbol(
      getSdInstance(),
      { propertyName, symbol, kuikaId: this.props.kuikaId, screenId: this.props.screenId },
      changeReason
    );
    this.notifyChanged(propertyName);
  };

  private setPropertyDataAction = (value: string | undefined) => {
    if (!this.canWrite) return undefined;
    const result = setDesignerPropertyDataAction(getSdInstance(), {
      kuikaId: this.props.kuikaId,
      propertyName: this.props.property.name,
      screenId: this.props.screenId,
      value
    });
    this.notifyChanged();
    return result;
  };

  private setPropertyDataField = (key: string, value: any, extra?: Record<string, any>) => {
    if (!this.canWrite) return undefined;
    const result = setDesignerPropertyDataField(getSdInstance(), {
      kuikaId: this.props.kuikaId,
      propertyName: this.props.property.name,
      screenId: this.props.screenId,
      key,
      value,
      ...(extra || {})
    });
    this.notifyChanged();
    return result;
  };

  // Chart fixed-color editor: opens the shared (position:fixed) styling ColorPicker
  // anchored to the click, so it escapes the property-panel Collapse clipping the
  // inline ui-kit picker suffered from. Only one picker is open at a time.
  private openChartColorPicker = (editorKey: string, event: React.MouseEvent<HTMLDivElement>): void => {
    if (this.state.activeColorEditorKey === editorKey) {
      this.setState({ activeColorEditorKey: undefined });
      return;
    }
    if (typeof event.persist === "function") {
      event.persist();
    }
    const mouseEvent: MouseEvent = event as unknown as MouseEvent;
    ColorPickerPositionHelper.saveLastClickEvent(mouseEvent);
    KMainFunctions.setColorTriggeredPickerPosition(mouseEvent);
    this.setState({ activeColorEditorKey: editorKey });
  };

  private closeChartColorPicker = (): void => {
    this.setState({ activeColorEditorKey: undefined });
  };

  applyPropertyTypeChanged = (value: number) => {
    if (!this.canWrite) return;
    setDesignerPropertyType(getSdInstance(), {
      propertyName: this.props.property.name,
      selectedType: Number(value),
      kuikaId: this.props.kuikaId,
      screenId: this.props.screenId
    });
    this.notifyChanged();
  };

  applyPropertyTypeOptionsChanged = (value: unknown) => {
    if (!this.canWrite) return;
    setDesignerPropertyTypeOptions(getSdInstance(), {
      propertyName: this.props.property.name,
      selectedTypeOptions: value,
      kuikaId: this.props.kuikaId,
      screenId: this.props.screenId
    });
    this.notifyChanged();
  };

  calculateCardinality = (): ECardinality => {
    const p = this.props.property;

    // For widget properties with isList (including primitive arrays like number[], string[])
    const isList = p.isList ?? p.dynamicPropertyDefinition?.isList;
    if (isList) {
      return ECardinality.Many;
    }

    if (this.propertyHelper.isItObject(p) === true) {
      return ECardinality.Many;
    }
    return ECardinality.One;
  };

  isItSymbolPicker = (calculatedType: string): boolean => {
    if (
      calculatedType === "Number" ||
      calculatedType === "Boolean" ||
      calculatedType === "String" ||
      calculatedType === "Image" ||
      calculatedType === "DateTime" ||
      calculatedType === "ColorPicker" ||
      calculatedType === "WidgetDomainType" ||
      calculatedType === "WidgetDomainTypeObject"
    ) {
      return true;
    }
    return false;
  };

  handleSymbolPlaceholderChange = (symbol: ISymbol, val: string) => {
    void symbol;
    void this.setPropertyDataField("valuePlaceholderText", val);
  };

  getStartingScreenValue = (): string => {
    const di = this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    const properties = di?.item?.properties as IProperty[] | undefined;
    const prop = properties?.find((p) => p.name === "startingScreen");
    const val = prop?.symbol?.fixedValue as string | undefined;
    return val ?? "";
  };

  setStartingScreenValue = (value: string) => {
    const trimmed = value?.trim?.() ?? "";
    this.setState({ isScreenInputsLoading: true });
    setDesignerMicroAppStartingScreen(getSdInstance(), {
      kuikaId: this.props.kuikaId,
      screenId: trimmed,
      propertyName: "startingScreen"
    }).then((result) => {
      this.setState({
        screenInputs: result?.inputs || [],
        isScreenInputsLoading: false
      });
    });
  };

  clearStartingScreenSelection = () => {
    this.setStartingScreenValue("");
    this.setState({
      startingScreenOptions: [],
      isStartingScreenOptionsLoading: false,
      screenInputs: [],
      isScreenInputsLoading: false
    });
  };

  private onClearAction = (propertyToolType: PropertyToolType) => {
    void propertyToolType;
    this.setAction(undefined, undefined);
  };

  setAction = (value: string | undefined, option: unknown) => {
    void option;
    this.setPropertyDataAction(value);
  };

  setFieldToLatitude = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fieldToLatitude", value);
  };

  setFieldToLongitude = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fieldToLongitude", value);
  };

  setFieldToDisplay = async (value: string | undefined, option: unknown, isTableField?: boolean, prePath?: string) => {
    void option;
    await this.setPropertyDataField(isTableField === true ? "tableFieldBinding" : "fieldToDisplay", value, {
      prePath
    });
  };

  setFieldToDisplayTree = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fieldToDisplay", value);
  };

  setTableEditModeRenderer = async (value: string | undefined, id?: string | number) => {
    await this.setPropertyDataField("tableEditModeRenderer", value, { targetId: id });
  };

  setTableDefaultColumnDatabindedValue = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fixedValue", value);
  };

  private getNormalizedFieldToDisplayValues = (value: string | string[] | undefined): string[] => {
    if (!value) {
      return [];
    }

    return Array.isArray(value) ? value : value.split(",").filter(Boolean);
  };

  setTableDataSourceType = (value: string, option: unknown, resetSymbol: boolean = true) => {
    void option;
    void this.setPropertyDataField("tableLabelSourceType", value, { resetSymbol });
  };

  setFieldToUseAsKey = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fieldToUseAsKey", value);
  };

  setFieldToUseAsParent = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fieldToUseAsParent", value);
  };

  setFieldToUseAsChecked = (value: string, option: unknown) => {
    void option;
    void this.setPropertyDataField("fieldToUseAsChecked", value);
  };

  setChartDataLabel = (value: string | undefined) => {
    void this.setPropertyDataField("chartDataLabel", value);
  };

  setChartTooltipFooterDataLabel = (value: string | undefined) => {
    void this.setPropertyDataField("chartTooltipFooterDataLabel", value);
  };

  setMultiTooltipContentField = (value: string | string[]) => {
    void this.setPropertyDataField("chartMultiTooltipContentField", value);
  };

  setCalendarField = (value: string, field: keyof CalendarOptions) => {
    void this.setPropertyDataField(`CalendarOptions.${field}`, value);
  };

  setMenuField = (value: string, field: keyof MenuOptions) => {
    void this.setPropertyDataField(`MenuOptions.${field}`, value);
  };

  setBreadcrumbField = (value: string, field: keyof BreadcrumbOptions) => {
    void this.setPropertyDataField(`BreadcrumbOptions.${field}`, value);
  };

  setRadialChartField = (value: string, field: keyof RadialChartOptions) => {
    void this.setPropertyDataField(`RadialChartOptions.${field}`, value);
  };

  setRadialChartLevelField = (value: string, field: keyof RadialChartLevelOptions) => {
    void this.setPropertyDataField(`RadialChartLevelOptions.${field}`, value);
  };

  setMixedChartField = (value: string, field: keyof MixedChartOptions) => {
    void this.setPropertyDataField(`MixedChartOptions.${field}`, value);
  };

  setMixedChartLineField = (value: string, field: keyof MixedChartLineOptions) => {
    void this.setPropertyDataField(`MixedChartLineOptions.${field}`, value);
  };

  setMixedChartPointField = (value: string, field: keyof MixedChartPointOptions) => {
    void this.setPropertyDataField(`MixedChartPointOptions.${field}`, value);
  };

  setTimelineField = (value: string, field: keyof TimelineOptions) => {
    void this.setPropertyDataField(`TimelineOptions.${field}`, value);
  };

  setChatField = (value: string, field: string) => {
    void this.setPropertyDataField(`ChatOptions.${field}`, value);
  };

  setKanbanBoardLaneField = (value: string | undefined, field: string) => {
    void this.setPropertyDataField(`KanbanBoardLaneOptions.${field}`, value);
  };

  setKanbanBoardCardField = (value: string | undefined, field: string) => {
    void this.setPropertyDataField(`KanbanBoardCardOptions.${field}`, value);
  };

  setNodeTreeNodeField = (value: string | undefined, field: string) => {
    void this.setPropertyDataField(`NodeTreeNodeOptions.${field}`, value);
  };

  setProjectIdField = (value: string) => {
    this.setState({ isStartingScreenOptionsLoading: true });
    setDesignerMicroAppProject(getSdInstance(), {
      kuikaId: this.props.kuikaId,
      projectId: value
    }).then((result) => {
      this.setState({
        startingScreenOptions: result?.screenOptions || [],
        isStartingScreenOptionsLoading: false,
        screenInputs: [],
        isScreenInputsLoading: false
      });
    });
  };

  setChartFirstDataField = (value: string) => {
    void this.setPropertyDataField("chartFirstDataField", value);
  };

  setChartFirstDataDisplayLabel = (value: string) => {
    void this.setPropertyDataField("chartDataDisplayLabel", value);
  };

  setChartTargetLineField = (value: string | undefined) => {
    void this.setPropertyDataField("chartTargetLineField", value);
  };

  setChartSecondDataField = (value: string) => {
    void this.setPropertyDataField("chartSecondDataField", value);
  };

  setChartThirdDataField = (value: string) => {
    void this.setPropertyDataField("chartThirdDataField", value);
  };

  clearChartSecondDataField = () => {
    void this.setPropertyDataField("chartSecondDataField", undefined);
  };

  clearChartThirdDataField = () => {
    void this.setPropertyDataField("chartThirdDataField", undefined);
  };

  setChartFirstDataFieldBgColor = (value: string) => {
    void this.setPropertyDataField("chartFirstDataBgColor", value);
  };

  getSelectedProjectId = (): string | undefined => {
    const di = this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    const projectIdProp = di?.item?.properties?.find((p) => p.name === "projectId");
    return projectIdProp?.symbol?.fixedValue as string | undefined;
  };

  getStartingPageOptions = (): { value: string; label: string }[] => {
    return this.state.startingScreenOptions;
  };

  getScreenInputSymbol = (inputName: string): ISymbol => {
    const di = this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    const properties = di?.item?.properties as IProperty[] | undefined;
    const propertyName = `screenInput_${inputName}`;
    const screenInputProp = properties?.find((p) => p.name === propertyName);

    if (screenInputProp?.symbol) {
      return screenInputProp.symbol;
    }

    return {
      category: ESymbolCategory.fixed,
      description: `Value for ${inputName}`,
      label: `Value for ${inputName}`,
      fixedValue: undefined
    };
  };

  onScreenInputSymbolChanged = (inputName: string, symbol: ISymbol) => {
    const propertyName = `screenInput_${inputName}`;
    this.symbolChangedForce(symbol, ESdChangeReason.PropertyFixedValueChanged, propertyName);
  };

  setChartSecondDataFieldBgColor = (value: string) => {
    void this.setPropertyDataField("chartSecondDataBgColor", value);
  };

  setChartThirdDataFieldBgColor = (value: string) => {
    void this.setPropertyDataField("chartThirdDataBgColor", value);
  };

  clearChartFirstDataFieldBgColor = () => {
    void this.setPropertyDataField("chartFirstDataBgColor", undefined);
  };

  clearChartFirstDataDisplayLabel = () => {
    void this.setPropertyDataField("chartDataDisplayLabel", undefined);
  };

  clearChartSecondDataFieldBgColor = () => {
    void this.setPropertyDataField("chartSecondDataBgColor", undefined);
  };

  clearChartThirdDataFieldBgColor = () => {
    void this.setPropertyDataField("chartThirdDataBgColor", undefined);
  };

  handleChangedDataKeyEditor = (value: string) => {
    void this.setPropertyDataField("fixedValue", value);
  };

  getSelectedDataAction = () => {
    const state = getDesignerPropertyDataEditorState(getSdInstance(), {
      kuikaId: this.props.kuikaId,
      propertyName: this.props.property.name,
      screenId: this.props.screenId
    });
    return state.success ? state.actionSelector?.value : undefined;
  };

  getPropertyDataEditorState = () => {
    return getDesignerPropertyDataEditorState(getSdInstance(), {
      kuikaId: this.props.kuikaId,
      propertyName: this.props.property.name,
      screenId: this.props.screenId
    });
  };

  // The action tree as the kit's tree select reads it. A node with children is a folder, which the
  // data source already marks disabled, so it opens and closes but is never the value; a node with
  // an empty child list is disabled and childless, exactly as antd drew it. `path` is the value
  // written into the property — the action's label at the top, a dotted field path below it. The
  // datasource is named in front only on screen: `name` is what the closed select shows too.
  getPropertyDataEditorTreeNodes = (
    options: DesignerPropertyDataEditorOption[],
    parentId: string = ""
  ): ITreeSelectNode[] => {
    return options.map((option: DesignerPropertyDataEditorOption, index: number): ITreeSelectNode => {
      const id: string = `${parentId}/${index}`;
      const hasChildren: boolean = (option.children?.length ?? 0) > 0;
      return {
        id,
        name: formatQualifiedName(option.label, option.datasourceQualifier),
        path: option.value,
        type: hasChildren ? "folder" : "leaf",
        disabled: option.disabled,
        testId: `selectDataActionNode-${option.value}`,
        children: hasChildren ? this.getPropertyDataEditorTreeNodes(option.children ?? [], id) : undefined
      };
    });
  };

  getPropertyDataEditorSelectOptions = (options: DesignerPropertyDataEditorOption[]): KuikaSelectboxOption[] => {
    return options.map((option: DesignerPropertyDataEditorOption, index: number): KuikaSelectboxOption => ({
      key: `${option.value}-${index}`,
      value: option.value,
      label: formatQualifiedName(option.label, option.datasourceQualifier),
      disabled: option.disabled,
      testId: `selectDataOption-${option.value}`
    }));
  };

  filterSelectableOption = (input: string, option: any) => {
    const optionText = option?.children?.toString().toLowerCase() || "";
    if (input && input.length > 1) {
      return optionText.includes(input.toLowerCase());
    }
    return optionText.startsWith(input.toLowerCase());
  };

  // Renders the data-editor list. Editors with no `group` render flat exactly as
  // before; consecutive editors sharing a `group` (e.g. a chart's per-series Data
  // Field / Bar Color / Legend) are wrapped in one titled section card so the
  // Series 1 / 2 / 3 boundaries are visually obvious.
  renderPropertyDataEditors = (
    state: DesignerPropertyDataEditorState,
    calculatedBackEndValueType: BackEndValueType,
    calculatedCardinality: ECardinality,
    targetDi?: IDisplayItem,
    parentDi?: IDisplayItem
  ): React.ReactNode => {
    const renderOne = (editor: DesignerPropertyDataFieldEditor): React.ReactNode => (
      <React.Fragment key={editor.key}>
        {this.renderPropertyDataEditorFieldLabel(editor)}
        {this.renderPropertyDataEditorField(
          editor,
          state,
          calculatedBackEndValueType,
          calculatedCardinality,
          targetDi,
          parentDi
        )}
      </React.Fragment>
    );

    const editors: DesignerPropertyDataFieldEditor[] = state.editors;
    const nodes: React.ReactNode[] = [];
    let i = 0;
    while (i < editors.length) {
      const editor: DesignerPropertyDataFieldEditor = editors[i];
      if (!editor.group) {
        nodes.push(renderOne(editor));
        i += 1;
        continue;
      }
      const groupKey: string = editor.group;
      const groupEditors: DesignerPropertyDataFieldEditor[] = [];
      while (i < editors.length && editors[i].group === groupKey) {
        groupEditors.push(editors[i]);
        i += 1;
      }
      const groupTitle: string | undefined = groupEditors[0].groupTitle;
      nodes.push(
        <div
          key={groupKey}
          className="kuika_propertyEditorGroup"
          style={{
            border: "var(--border)",
            background: "var(--dark-1)",
            borderRadius: "var(--radius-s)",
            padding: "6px 10px 10px",
            marginTop: 10
          }}
        >
          {groupTitle && (
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "var(--dark-7)",
                marginBottom: 2
              }}
            >
              {groupTitle}
            </div>
          )}
          {groupEditors.map((groupEditor) => renderOne(groupEditor))}
        </div>
      );
    }
    return nodes;
  };

  renderPropertyDataEditorFieldLabel = (editor: DesignerPropertyDataFieldEditor) => {
    if (editor.key === "fixedValue" || editor.key === "valuePlaceholderText" || editor.hideLabel === true) {
      return null;
    }

    return (
      <label className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginTop: 8, marginBottom: 6 }}>
        {editor.label}
        {editor.required && <span className="kuika-property-field-required"> *</span>}
      </label>
    );
  };

  renderPropertyDataEditorField = (
    editor: DesignerPropertyDataFieldEditor,
    state: DesignerPropertyDataEditorState,
    calculatedBackEndValueType: BackEndValueType,
    calculatedCardinality: ECardinality,
    targetDi?: IDisplayItem,
    parentDi?: IDisplayItem
  ): React.ReactNode => {
    const isDesktopSelectBoxField =
      editor.key === "fieldToDisplay" &&
      state.propertyToolType === "Options" &&
      this.sdTarget?.project?.projectType === EProjectType.Desktop &&
      targetDi?.item?.name === "SelectBox";

    const isTableSourceTypeEditor = editor.key === "tableLabelSourceType";
    const isTableBindingEditor = editor.key === "tableFieldBinding";
    const isTableEditModeRendererEditor = editor.key === "tableEditModeRenderer";
    const isValuePlaceholderEditor = editor.key === "valuePlaceholderText";
    const isFixedValueEditor = editor.key === "fixedValue";
    const isTableColumnEditor =
      parentDi?.item?.name === "EditableTableColumn" || parentDi?.item?.name === "TableDefaultColumn";

    if (editor.kind === "text") {
      if (isFixedValueEditor) {
        return (
          <DataKeyTextEditor
            key={editor.key}
            value={typeof editor.value === "string" ? editor.value : ""}
            onChange={(value) => this.setPropertyDataField(editor.key, value)}
          ></DataKeyTextEditor>
        );
      }

      if (isValuePlaceholderEditor) {
        return (
          <KuikaInput
            key={editor.key}
            defaultValue={typeof editor.value === "string" ? editor.value : undefined}
            placeholder="Design Placeholder"
            style={{ marginTop: 12, width: "100%" }}
            onBlur={(e) => this.handleSymbolPlaceholderChange(this.props.property.symbol, e.target.value)}
          />
        );
      }

      if (editor.key === "CalendarOptions.avatarMaxCount") {
        return (
          <KuikaInput
            key={editor.key}
            type="number"
            min={1}
            defaultValue={editor.value != null ? String(editor.value) : undefined}
            placeholder="Max avatars (e.g. 3)"
            style={{ marginTop: 12, width: "100%" }}
            onBlur={(e) => {
              void this.setPropertyDataField(editor.key, e.target.value ? e.target.value : undefined);
            }}
          />
        );
      }

      return (
        <KuikaInput
          key={`${editor.key}-${typeof editor.value === "string" ? editor.value : ""}`}
          defaultValue={typeof editor.value === "string" ? editor.value : undefined}
          placeholder={editor.label}
          style={{ marginTop: 12, width: "100%" }}
          onBlur={(e) => this.setPropertyDataField(editor.key, e.target.value)}
        />
      );
    }

    if (editor.kind === "color") {
      const colorValue: string = typeof editor.value === "string" ? editor.value : "";
      const isPickerOpen: boolean = this.state.activeColorEditorKey === editor.key;
      return (
        <div key={editor.key} className="kuika-property-color-field">
          <div
            data-testid={`${editor.key}ColorSwatch`}
            className="kuika-property-color-field__trigger"
            onClick={(e) => this.openChartColorPicker(editor.key, e)}
          >
            <div className="kuika-property-color-field__swatch" style={{ background: colorValue || "transparent" }} />
            <span className="kuika-property-color-field__value">{colorValue || "Select color"}</span>
          </div>
          <div className="kuika_colorPicker" style={{ display: isPickerOpen ? "block" : "none" }}>
            <ColorPicker
              color={colorValue}
              isColorPickerActive={isPickerOpen}
              isHex
              hideHeaderTabs
              isSavedColorsHidden
              onChange={(value: string) => this.setPropertyDataField(editor.key, value)}
              onCancel={this.closeChartColorPicker}
            />
          </div>
        </div>
      );
    }

    if (editor.kind === "tabs") {
      return (
        <div key={editor.key} style={{ marginBottom: 4 }}>
          <KuikaTabs
            size="sm"
            value={typeof editor.value === "string" ? editor.value : undefined}
            options={(editor.options || []).map((o) => ({ value: o.value, label: o.label }))}
            onChange={(value: string) => this.setPropertyDataField(editor.key, value)}
          />
        </div>
      );
    }

    if (editor.kind === "multiselect") {
      return (
        <KuikaSelectbox
          key={editor.key}
          data-testid={`${editor.key}Editor`}
          mode="tags"
          style={{ width: "100%" }}
          value={(editor.value as string[] | undefined) || []}
          onChange={(value: string[]) => this.setPropertyDataField(editor.key, value)}
          allowClear={editor.allowClear}
          searchable
          tokenSeparators={[","]}
          filterOption={this.filterSelectableOption}
          options={this.getPropertyDataEditorSelectOptions(editor.options || [])}
        />
      );
    }

    // Which handler a single pick goes to depends on the field. The selectbox reports a cleared
    // field as `null` where antd said `undefined`, and the three field handlers below test for
    // `undefined`; the value is translated at this one boundary so they see what they always did.
    let handleSingleChange: ((value: string | undefined) => void) | undefined;
    if (isTableEditModeRendererEditor) {
      handleSingleChange = (value: string | undefined): void =>
        this.handleTableEditModeComponentChange(value as string);
    } else if (isTableBindingEditor) {
      handleSingleChange = (value: string | undefined): void => this.handleFieldToDisplayChange(value as string);
    } else if (isFixedValueEditor) {
      handleSingleChange = (value: string | undefined): void => this.handleFieldToUseAsDefaultChange(value as string);
    }

    if (isDesktopSelectBoxField) {
      return (
        <KuikaSelectbox
          key={editor.key}
          data-testid={`${editor.key}Editor`}
          mode="multiple"
          style={{ width: "100%" }}
          disabled={isTableSourceTypeEditor && isTableColumnEditor}
          value={this.getNormalizedFieldToDisplayValues(editor.value as string | string[] | undefined) ?? []}
          onChange={(value: string[]): void => {
            const normalizedValue: string | undefined = value.length === 0 ? undefined : value.join(",");
            void this.setPropertyDataField(editor.key, normalizedValue);
          }}
          onClear={(): void => {
            void this.setPropertyDataField(editor.key, undefined);
          }}
          allowClear={editor.allowClear}
          searchable={editor.kind === "select"}
          filterOption={this.filterSelectableOption}
          options={this.getPropertyDataEditorSelectOptions(editor.options || [])}
        />
      );
    }

    return (
      <KuikaSelectbox
        key={editor.key}
        data-testid={`${editor.key}Editor`}
        style={{ width: "100%" }}
        disabled={isTableSourceTypeEditor && isTableColumnEditor}
        value={(editor.value as string | number | undefined) ?? null}
        onSelect={(value: string | number | null): void => {
          void this.setPropertyDataField(editor.key, value);
        }}
        onChange={
          handleSingleChange ? (value: string | null): void => handleSingleChange?.(value ?? undefined) : undefined
        }
        onClear={(): void => {
          void this.setPropertyDataField(editor.key, undefined);
        }}
        allowClear={editor.allowClear}
        searchable={editor.kind === "select"}
        filterOption={this.filterSelectableOption}
        options={this.getPropertyDataEditorSelectOptions(editor.options || [])}
      />
    );
  };

  renderPropertyDataEditor = (
    state: DesignerPropertyDataEditorState,
    calculatedBackEndValueType: BackEndValueType,
    calculatedCardinality: ECardinality,
    targetDi?: IDisplayItem,
    parentDi?: IDisplayItem
  ) => {
    const p: IProperty = this.props.property;

    if (state.symbolPicker && this.props.property.symbol.tableLabelSourceType == undefined) {
      this.setTableDataSourceType("tableBinding", undefined, false);
    }

    return (
      <>
        <div className="kuika_property-grid-column kuika-right-panel__col">
          {state.infoMessage && (
            <KuikaInfoBox
              color="blue"
              leftUnit={<KuikaSvg name="info-circle" width={16} height={16} />}
              rightUnit={<KuikaSmallText>{state.infoMessage}</KuikaSmallText>}
            />
          )}
          {state.actionSelector && (
            <>
              <label
                data-testid="selectDataActionLabel"
                className="k_Regular-G1-Left kuika_rightPanelDIName"
                style={{ marginBottom: 5 }}
              >
                Action
              </label>
              {state.actionSelector.kind === "tree" ? (
                <KuikaTreeSelect
                  multiple={false}
                  popup="floating"
                  dataTestId="selectDataActionSelector"
                  value={state.actionSelector.value}
                  placeholder="Please select"
                  allowClear={state.actionSelector.allowClear}
                  searchable
                  showLine
                  // A pick of the action already chosen writes the same action again, so the kit not
                  // reporting a repeat pick changes nothing; a clear arrives as `undefined`, as before.
                  onChange={(value: string | undefined): void => this.setAction(value, undefined)}
                  treeData={this.getPropertyDataEditorTreeNodes(state.actionSelector.options)}
                />
              ) : (
                <KuikaSelectbox
                  data-testid="selectDataActionSelector"
                  value={state.actionSelector.value ?? null}
                  style={{ width: "100%" }}
                  allowClear={state.actionSelector.allowClear}
                  onSelect={(value: string | number | null): void =>
                    this.setAction(value === null ? undefined : String(value), undefined)
                  }
                  onClear={() => this.setAction(undefined, undefined)}
                  options={this.getPropertyDataEditorSelectOptions(state.actionSelector.options)}
                />
              )}
            </>
          )}

          {this.renderPropertyDataEditors(state, calculatedBackEndValueType, calculatedCardinality, targetDi, parentDi)}
        </div>

        {state.symbolPicker?.enabled && (
          <div className="kuika-right-panel__col" style={{ alignSelf: "center" }}>
            <SymbolPicker
              symbol={_.cloneDeep(this.props.property.symbol)}
              valueType={calculatedBackEndValueType}
              targetCardinality={calculatedCardinality}
              onSelectedSymbol={(symbol: ISymbol) => this.onSymbolPicked(symbol)}
              isValueProp={p.isValueProp}
              hideFixedValue={this.props.property?.hideFixedValue}
              property={this.props.property}
            ></SymbolPicker>
          </div>
        )}
      </>
    );
  };

  handleFieldToDisplayChange = (value: string) => {
    if (value === undefined) {
      void this.setPropertyDataField("tableFieldBinding", undefined);
    }
  };

  handleFieldToUseAsDefaultChange = (value: string) => {
    void this.setPropertyDataField("fixedValue", value);
  };

  handleTableEditModeComponentChange = (value: string) => {
    if (value === undefined) {
      void this.setPropertyDataField("tableEditModeRenderer", undefined);
    }
  };

  renderNotFoundContent = (isLoading: boolean): React.ReactNode | undefined => {
    if (isLoading) {
      return "Loading...";
    }

    return undefined;
  };

  getIsNestedDataRepeaterOptionsProperty = (di: IDisplayItem): boolean => {
    if (!di?.item?.name) return false;

    const componentDefinition = this.sdTarget?.getComponentDefinition?.(di.item);
    const dataRepeaterParent = this.getDirectDataRepeaterParent(di);

    return !!componentDefinition?.isDataRepeater && dataRepeaterParent !== undefined;
  };

  getDirectDataRepeaterParent = (di: IDisplayItem): IDisplayItem | undefined => {
    if (!di.parentId) {
      return undefined;
    }

    const parentDi = this.sdTarget?.screenDesignDictionary?.[di.parentId];

    if (!parentDi) {
      return undefined;
    }

    const componentDefinition: IComponentDefinition | undefined = this.sdTarget?.getComponentDefinition?.(
      parentDi?.item
    );

    if (
      componentDefinition?.name !== "TableTemplateColumn" &&
      componentDefinition?.name !== "TableColumn" &&
      componentDefinition?.name !== "EditableTableColumn" &&
      componentDefinition?.name !== "TableDefaultColumn" &&
      componentDefinition?.name !== "TableRow" &&
      componentDefinition?.name !== "ReportTableColumn" &&
      componentDefinition?.name !== "ReportTableRow" &&
      componentDefinition?.name !== "ContentMenu" &&
      componentDefinition?.isDataRepeater
    ) {
      return parentDi;
    }

    return this.getDirectDataRepeaterParent(parentDi);
  };

  renderFormItem = () => {
    let calculatedPropertyToolType: PropertyToolType = this.calculatePropertyToolType();
    let calculatedBackEndValueType: BackEndValueType = this.calculateBackEndValueType();
    let calculatedCardinality = this.calculateCardinality();
    const isItSymbolPicker = this.isItSymbolPicker(calculatedPropertyToolType);
    const p: IProperty = this.props.property;
    const targetDi: IDisplayItem | undefined =
      this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    const parentDi = targetDi?.parentId ? this.sdTarget?.screenDesignDictionary?.[targetDi.parentId] : undefined;

    // Decimal için "property helper => calculateBackEndValueType" methodu içinde bir yere ihtiyacımız olacak
    const diListForInt16ToDecimal = ["LocationPicker", "NumberInput"];
    if (calculatedBackEndValueType === "Int64" && diListForInt16ToDecimal.includes(targetDi?.item.name || "")) {
      calculatedBackEndValueType = "Decimal";
    }

    if (
      targetDi &&
      parentDi &&
      (calculatedPropertyToolType === "Options" || calculatedPropertyToolType === "TableOptions") &&
      this.getIsNestedDataRepeaterOptionsProperty(targetDi)
    ) {
      calculatedPropertyToolType = "NestedTableOptions";
      calculatedBackEndValueType = "Array";
      calculatedCardinality = ECardinality.One;
    }

    const propertyDataEditorState = this.getPropertyDataEditorState();
    if (propertyDataEditorState.success) {
      if (targetDi && targetDi.item?.name?.toLowerCase() != "label" && calculatedPropertyToolType === "Image") {
        calculatedPropertyToolType = "String";
      }

      return this.renderPropertyDataEditor(
        propertyDataEditorState,
        calculatedBackEndValueType,
        calculatedCardinality,
        targetDi,
        parentDi
      );
    }

    // The two MicroApp editors write through calls whose own `screenId` argument already means
    // something else (the MicroApp's STARTING screen), so they cannot be pointed at another
    // screen. On a screen the designer does not have open they say so; a write here would land on
    // the canvas while the user was looking at a different screen.
    if (
      this.props.screenId &&
      (calculatedPropertyToolType === "MicroAppProjectProp" ||
        calculatedPropertyToolType === "MicroAppStartingScreenProp")
    ) {
      return (
        <KuikaSmallText style={{ color: "var(--dark-9)" }} data-testid="propertyFormMicroAppNotEditableHere">
          Open this screen to choose its MicroApp project and starting screen.
        </KuikaSmallText>
      );
    }

    if (calculatedPropertyToolType === "MicroAppProjectProp") {
      const searchText = this.state.projectSearchText.toLowerCase();
      const filteredProjects = this.props.sd.projects
        .filter((project) => project.id !== this.props.sd.getProject().id)
        .filter((project) => {
          if (!searchText) return true;
          const projectName = (project.name || "").toLowerCase();
          if (searchText.length > 1) {
            return projectName.includes(searchText);
          }
          return projectName.startsWith(searchText);
        });

      return (
        <div className="kuika_property-grid-column kuika-right-panel__col">
          <KuikaSelectbox
            value={this.props.property.symbol.fixedValue ?? null}
            style={{ width: "100%" }}
            placeholder="Choose a project"
            notFoundContent={this.renderNotFoundContent(
              this.props.sd.isProjectsLoading && !this.props.sd.isProjectsLoaded
            )}
            virtual={false}
            searchable
            // The list below is already narrowed to the search text, so the selectbox must not narrow
            // it a second time. antd took `false` for that; the kit takes a filter that keeps all.
            filterOption={(): boolean => true}
            onSearch={(value: string): void => this.setState({ projectSearchText: value })}
            onSelect={(value: string | number | null): void => {
              if (value) {
                const pid: string = value.toString();
                this.setProjectIdField(pid);
                this.setState({ projectSearchText: "" });
              }
            }}
            onVisibleChange={(open: boolean): void => {
              if (!open) {
                this.setState({ projectSearchText: "" });
              }
            }}
            options={filteredProjects.map((project): KuikaSelectboxOption => ({
              key: project.id,
              value: project.id,
              label: project.name
            }))}
          />
        </div>
      );
    }

    if (calculatedPropertyToolType === "MicroAppStartingScreenProp") {
      return (
        <>
          <div className="kuika_property-grid-column kuika-right-panel__col">
            <KuikaSelectbox
              style={{ width: "100%" }}
              value={this.getStartingScreenValue() || null}
              placeholder="Select a starting page"
              allowClear
              searchable
              disabled={this.state.isStartingScreenOptionsLoading}
              onSelect={(value: string | number | null): void => this.setStartingScreenValue(value as string)}
              onClear={() => this.setStartingScreenValue("")}
              // Same rule as the data editors: one letter matches the start of a name, more match
              // anywhere in it.
              filterOption={this.filterSelectableOption}
              options={(this.state.startingScreenOptions ?? []).map((o): KuikaSelectboxOption => ({
                key: o.value,
                value: o.value,
                label: o.label
              }))}
            />
          </div>

          {/* Render SymbolPicker for each screen input */}
          {this.state.screenInputs.length > 0 && (
            <>
              <div className="kuika_property-grid-column kuika-right-panel__col">
                <div style={{ marginTop: 16, marginBottom: 8 }}>
                  <label className="k_Regular-G1-Left kuika_rightPanelDIName">Screen Inputs:</label>
                </div>
              </div>

              {this.state.screenInputs.map((input, index) => {
                return (
                  <div key={`screen-input-${index}`} className="kuika_property-grid-column kuika-right-panel__col">
                    <div style={{ marginBottom: 12 }}>
                      <label className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ marginBottom: 6 }}>
                        {input.name} ({input.typeName})
                      </label>
                      <SymbolPicker
                        changeType="onBlur"
                        key={Guid.create().toString()}
                        symbol={this.getScreenInputSymbol(input.name)}
                        valueType={input.typeName as BackEndValueType}
                        targetCardinality={input.cardinality as ECardinality}
                        onSelectedSymbol={(symbol: ISymbol) => this.onScreenInputSymbolChanged(input.name, symbol)}
                        property={this.props.property}
                      />
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Show loading state for screen inputs */}
          {this.state.isScreenInputsLoading && (
            <div className="kuika_property-grid-column kuika-right-panel__col">
              <div className="kuika-property-screen-inputs-loading">Loading screen inputs...</div>
            </div>
          )}
        </>
      );
    }

    if (calculatedPropertyToolType === "DynamicFormEntity") {
      return (
        <div className="kuika_property-grid-column kuika-right-panel__col">
          <SymbolPicker
            symbol={_.cloneDeep(this.props.property.symbol)}
            valueType={"Unknown"}
            targetCardinality={calculatedCardinality}
            onSelectedSymbol={(symbol: ISymbol) => this.onSymbolPicked(symbol)}
            isValueProp={p.isValueProp}
            isObject={true}
            property={this.props.property}
          />
        </div>
      );
    }

    if (calculatedPropertyToolType === "DynamicFormAttributes") {
      return (
        <div className="kuika_property-grid-column kuika-right-panel__col">
          <SymbolPicker
            symbol={_.cloneDeep(this.props.property.symbol)}
            valueType={"Unknown"}
            targetCardinality={ECardinality.Many}
            onSelectedSymbol={(symbol: ISymbol) => this.onSymbolPicked(symbol)}
            isValueProp={p.isValueProp}
            isObject={true}
            property={this.props.property}
          />
        </div>
      );
    }

    if (calculatedPropertyToolType === "DynamicFormValue") {
      return (
        <div className="kuika_property-grid-column kuika-right-panel__col">
          <SymbolPicker
            symbol={_.cloneDeep(this.props.property.symbol)}
            valueType={"Unknown"}
            targetCardinality={calculatedCardinality}
            onSelectedSymbol={(symbol: ISymbol) => this.onSymbolPicked(symbol)}
            isValueProp={p.isValueProp}
            isObject={true}
            property={this.props.property}
          />
        </div>
      );
    }

    if (calculatedPropertyToolType === "DynamicFormatter") {
      const textInputFormatter =
        this.sdTarget?.project?.componentSetObj?.components
          .find((c) => c.tagName === "TextInput")
          ?.properties.find((p) => p.name === "formatter")?.options ?? [];
      const dateInputFormatter =
        this.sdTarget?.project?.componentSetObj?.components
          .find((c) => c.tagName === "KDatePicker")
          ?.properties.find((p) => p.name === "format")?.options ?? [];
      const typesWithOptions = [
        { type: "String", options: textInputFormatter },
        { type: "Date Time", options: dateInputFormatter }
      ];
      const selectedTypeOptions = p.selectedTypeOptions ?? [];

      if (typesWithOptions.length > 0) {
        return (
          <PropertyFormDynamicInputFormatter
            allTypeOptions={typesWithOptions}
            selectedTypeOptions={selectedTypeOptions}
            onChange={(value: any) => {
              this.applyPropertyTypeOptionsChanged(value);
            }}
            changeType={"onBlur"}
            updateDebounce={0}
          />
        );
      }
    }

    return (
      <>
        {this.propertyHelper.needSelectPropertyType(p) && (
          <div className="kuika_property-grid-column kuika-right-panel__col">
            <KuikaSelectbox
              value={p.selectedType ?? null}
              className="kuika_selectPropertyType_property"
              placeholder="Please select a type"
              onChange={(newValue: string | number | null): void => {
                this.applyPropertyTypeChanged(Number(newValue));
              }}
              options={this.propertyHelper
                .getPropertyTypeNamesFromProperty(p)
                .map((o, idx: number): KuikaSelectboxOption => ({ key: `pt_${idx}`, value: o.value, label: o.name }))}
            />
          </div>
        )}
        {isItSymbolPicker === true && (
          <div className="kuika-right-panel__col" style={{ alignSelf: "center" }}>
            <SymbolPicker
              symbol={_.cloneDeep(this.props.property.symbol)}
              valueType={calculatedBackEndValueType}
              targetCardinality={calculatedCardinality}
              onSelectedSymbol={(symbol: ISymbol) => this.onSymbolPicked(symbol)}
              isValueProp={p.isValueProp}
              isWidthOrHeightProp={PropertyHelper.getUnitInputPropertyName(this.props.property?.name)}
              hideFixedValue={this.props.property?.hideFixedValue}
              isColorPicker={calculatedPropertyToolType === "ColorPicker"}
              property={this.props.property}
            ></SymbolPicker>
          </div>
        )}

        {calculatedPropertyToolType === "Icon" && (
          <PropertyFormIcon
            onChange={this.fixedValueChanged}
            name={this.props.property?.symbol?.fixedValue}
            iconLibrary={this.getResolvedIconLibrary()}
            iconType={this.getResolvedIconType()}
            onLibraryChange={this.onIconLibraryChanged}
            onIconTypeSync={this.onIconTypeSync}
            supportsLibrary={this.componentSupportsIconLibrary()}
          />
        )}
        {calculatedPropertyToolType === "Select" && (
          <PropertyFormSelectProperty
            onChange={this.fixedValueChanged}
            onCustomValueChange={this.customValueChanged}
            value={this.props.property?.symbol?.fixedValue}
            customValue={this.getCustomPropertyValue("customFormatter")}
            options={this.getResolvedSelectOptions().map((o) => ({
              value: o,
              label: o
            }))}
            sd={this.props.sd}
          />
        )}
        {calculatedPropertyToolType === "Tags" && (
          <PropertyFormSelectProperty
            mode="tags"
            onChange={this.tagsValueChanged}
            value={this.props.property?.symbol?.fixedValue}
            options={this.getResolvedSelectOptions().map((o) => ({
              value: o,
              label: o
            }))}
            sd={this.props.sd}
          />
        )}
        {/* {calculatedPropertyToolType === "ResponsiveColumn" && <PropertyFormColProperty kuikaId={kuikaId} property={p} isAllResponsiveColumnsSizeSwitchEnabled={this.props.isAllResponsiveColumnsSizeSwitchEnabled} applyPropertyValueChanged={this.fixedValueChanged} gridSystemCount={this.props.sd.project.componentSetObj.gridSystemCount} />} */}
        {calculatedPropertyToolType === "Enum" && (
          <PropertyFormEnumProperty applyPropertyValueChanged={this.fixedValueChanged} property={this.props.property} />
        )}
        {calculatedPropertyToolType === "ColorPickerArray" && (
          <PropertyFormColorPickerArrayProperty
            applyPropertyValueChanged={this.fixedValueChanged}
            property={this.props.property}
          />
        )}

        {calculatedPropertyToolType === "JSONEditor" && (
          <div
            className="kuika-right-panel__col kuika-right-panel__col--aside"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: "4px"
            }}
            onClick={() => this.props.toggleJsonModal(p)}
          >
            <svg
              className="kuika_hoverableSvg"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
            >
              <path d="M11.916 0c-.533 0-1.066.203-1.474.61l-.863.864 2.947 2.947.864-.863c.813-.814.813-2.134 0-2.948-.407-.407-.94-.61-1.474-.61zM8.474 2.579L0 11.053V14h2.947l8.474-8.474L8.474 2.58z" />
            </svg>
          </div>
        )}
        {/* {calculatedType === "Style" && <PropertyFormInputMultilineProperty value={this.props.property?.symbol?.fixedValue} onChange={this.handleValueChanged} /> } */}
      </>
    );
  };

  /** The display item these properties belong to, on whichever screen this row is editing. */
  private get targetDisplayItem(): IDisplayItem | undefined {
    return this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
  }

  calculatePropertyToolType = (): PropertyToolType => {
    // The platform's tool-type calculation DEREFERENCES the display item (`isMicroAppProjectProp`
    // reads `di.item.name` unguarded), so it must never be handed a missing one — `render` returns
    // before reaching here in that case.
    const di: IDisplayItem | undefined = this.targetDisplayItem;
    if (di === undefined) return "String" as PropertyToolType;
    const p: IProperty = this.props.property;
    return this.propertyHelper.calculatePropertyToolType(di, p);
  };

  calculateBackEndValueType = (): BackEndValueType => {
    const p: IProperty = this.props.property;
    return this.propertyHelper.calculateBackEndValueType(p);
  };

  onSymbolPicked = (symbol: ISymbol) => {
    if (
      !symbol.category ||
      (symbol.category === ESymbolCategory.fixed &&
        this.props.property.symbol.category === ESymbolCategory.fixed &&
        symbol.fixedValue !== this.props.property.symbol.fixedValue)
    ) {
      this.fixedValueChanged(symbol.fixedValue);
    } else {
      this.symbolChanged(symbol, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    }
  };

  getProperties = (): IProperty[] | undefined => {
    if (this.props.kuikaId === undefined) {
      return [];
    }
    let properties: IProperty[] | undefined;
    const { kuikaId } = this.props;
    properties = this.propertySdHelper.getProperties(kuikaId);
    return properties;
  };

  getCustomPropertyValue = (propertyName: string): any => {
    const di = this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    if (!di) return;
    const properties = di.item.properties;
    const property = properties?.find((p) => p.name === propertyName);
    return property?.symbol?.fixedValue;
  };

  symbolChanged = (symbol: ISymbol, changeReason: ESdChangeReason) => {
    this.writeSymbol(this.props.property.name, symbol, changeReason);
  };

  symbolChangedForce = (symbol: ISymbol, changeReason: ESdChangeReason, propertyName: string) => {
    this.writeSymbol(propertyName, symbol, changeReason);
  };

  fixedValueChanged = (value: any) => {
    this.writeValue(this.props.property.name, value);
  };

  // `accept` holds HTML accept tokens — canonicalize them (".Pdf"/"pdf" → ".pdf",
  // invalid tokens dropped). Other tags-input properties store the raw comma-join.
  tagsValueChanged = (value: string) => {
    if (this.props.property.name !== "accept") {
      this.fixedValueChanged(value);
      return;
    }
    const tokens: string[] = value ? value.split(",") : [];
    this.fixedValueChanged(normalizeAcceptValue(tokens));
  };

  getSiblingPropertyValue = (propertyName: string): unknown => {
    const di = this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    const siblingProp = di?.item?.properties?.find((p) => p.name === propertyName);
    return siblingProp?.symbol?.fixedValue;
  };

  private getSiblingStringValue = (propertyName: string): string | undefined => {
    const value: unknown = this.getSiblingPropertyValue(propertyName);
    return typeof value === "string" ? value : undefined;
  };

  private getResolvedIconLibrary = (): IconLibrary | undefined => {
    const raw: string | undefined = this.getSiblingStringValue("iconLibrary");
    if (raw === "material" || raw === "fontawesome") return raw;
    return undefined;
  };

  // True only when the owning component declares an `iconLibrary` property — i.e.
  // the `Icon` component, whose runtime renders Font Awesome. Button/FloatButton
  // icons render Material-only, so the picker must not offer the FA library.
  private componentSupportsIconLibrary = (): boolean => {
    const di = this.sdTarget?.screenDesignDictionary?.[parseInt(this.props.kuikaId, 10)];
    return di?.item?.properties?.some((p) => p.name === "iconLibrary") ?? false;
  };

  private getResolvedIconType = (): IconType | undefined => {
    const raw: string | undefined = this.getSiblingStringValue("iconType");
    if (isMaterialIconType(raw) || isFontAwesomeIconType(raw)) return raw;
    return undefined;
  };

  getResolvedSelectOptions = (): string[] => {
    const p = this.props.property;
    const fallback: string[] = p.options || [];
    const dependsOn: string | undefined = p.optionsDependsOn;
    const byDependency: Record<string, string[]> | undefined = p.optionsByDependency;
    if (!dependsOn || !byDependency) {
      return fallback;
    }
    const siblingValue: string | undefined = this.getSiblingStringValue(dependsOn);
    const key: string = siblingValue && siblingValue.length > 0 ? siblingValue : p.defaultValue;
    const filtered: string[] | undefined = byDependency[key];
    if (Array.isArray(filtered) && filtered.length > 0) {
      return filtered;
    }
    return fallback;
  };

  onIconLibraryChanged = (library: IconLibrary): void => {
    this.writeValue("iconLibrary", library, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    const currentIconType: string | undefined = this.getSiblingStringValue("iconType");
    const validForNewLibrary: boolean =
      library === "material" ? isMaterialIconType(currentIconType) : isFontAwesomeIconType(currentIconType);
    if (!validForNewLibrary) {
      const defaultType: IconType = library === "material" ? MATERIAL_ICON_TYPES[0] : FA_ICON_TYPES[0];
      this.writeValue("iconType", defaultType, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    }
  };

  onIconTypeSync = (iconType: IconType): void => {
    const current: string | undefined = this.getSiblingStringValue("iconType");
    if (current === iconType) return;
    this.writeValue("iconType", iconType, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
  };

  customValueChanged = (value: any, propName: string) => {
    this.writeValue(propName, value);
  };

  shouldHidePropertyRow = (): boolean => {
    if (this.props.property?.name !== "iconType") return false;
    const library: string | undefined = this.getSiblingStringValue("iconLibrary");
    return library === "fontawesome";
  };

  render() {
    const p: IProperty = this.props.property;
    // Nothing to edit when the component is not on the screen this row is pointed at: the screen
    // was closed (a detached session dropped) or the component was just deleted. Rendering on is
    // not an option — the platform's tool-type calculation dereferences the display item — and
    // reading the OPEN screen instead would edit a screen the user is not looking at.
    if (this.targetDisplayItem === undefined) return <></>;
    const calculatedPropertyToolType: PropertyToolType = this.calculatePropertyToolType();
    if (calculatedPropertyToolType === "ResponsiveColumn") return <></>;
    if (this.shouldHidePropertyRow()) return <></>;
    return (
      <div>
        <KuikaTooltip content={this.props.property.description}>
          <div className="kuika_property-grid-row kuika-right-panel__row">
            <div className="kuika_property-grid-column kuika-right-panel__col" style={{ alignSelf: "center" }}>
              <div style={{ textOverflow: "ellipsis", overflow: "hidden" }} className="kuika_rightPanelDIPanel">
                {this.renderFormItem()}
              </div>
            </div>
          </div>
        </KuikaTooltip>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason, nextProps?: IPropertyFormItemProps): boolean => {
    // See `refreshToken`: for a detached screen this is the only signal that the value shown here
    // is stale.
    if (nextProps !== undefined && nextProps.refreshToken !== this.props.refreshToken) {
      return true;
    }
    if (
      changeReason === ESdChangeReason.ChangePropertyType ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
      changeReason === ESdChangeReason.ChangeResponsiveRowColumnSettings ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    if (changeReason === ESdChangeReason.ChangePropertyValue && this.props.property?.optionsDependsOn) {
      return true;
    }
    return false;
  };
}

const propertyFormItem = withContext<IPropertyFormItemProps>(withSD<IPropertyFormItemProps>(PropertyFormItem));
export { propertyFormItem as PropertyFormItem };
// Unwrapped class (no SD/context HOC) — for unit tests only, matching the convention in
// `data-diagram/create-connection.tsx`. The HOCs only inject `sd` / `context`, so a test supplies
// them directly and can then assert what a write actually addresses.
export { PropertyFormItem as PropertyFormItemClass };
